import React, { useState, useEffect } from "react";
import apiClient from "../../AppClient";
import { Container, Form, Button, Table, Alert, Spinner, Modal } from "react-bootstrap";

const FertilizationsReport = () => {
  const [fertilizations, setFertilizations] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedFertilization, setSelectedFertilization] = useState(null);
  const [dateFilter, setDateFilter] = useState("");
  const [nameFilter, setNameFilter] = useState("");

  // Fetch all fertilizations
  const fetchFertilizations = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get("/transactions/fertilizations/");
      setFertilizations(response.data);
      setError(null);
    } catch (error) {
      setError(error.response?.data?.detail || "فشل في جلب عمليات التسميد.");
    } finally {
      setLoading(false);
    }
  };

  // Filter fertilizations based on date and fertilizer name
  const filteredFertilizations = fertilizations.filter((fertilization) => {
    const matchesDate = dateFilter 
      ? fertilization.transaction_date.includes(dateFilter) 
      : true;
    
    const matchesName = nameFilter 
      ? fertilization.fertilizer.name.toLowerCase().includes(nameFilter.toLowerCase()) 
      : true;
    
    return matchesDate && matchesName;
  });

  // Calculate total fertilizations amount
  const totalFertilizations = filteredFertilizations.reduce(
    (total, fertilization) => total + parseFloat(fertilization.amount),
    0
  ).toFixed(2);

  // Open details modal
  const viewFertilizationDetails = (fertilization) => {
    setSelectedFertilization(fertilization);
    setShowDetails(true);
  };

  useEffect(() => {
    fetchFertilizations();
  }, []);

  return (
    <Container style={{ 
      direction: "rtl", 
      textAlign: "right", 
      padding: "20px", 
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      color: "black", 
      backgroundColor: "white" 
    }}>
      <h1 style={{ color: "darkgreen", marginBottom: "20px" }}>تقرير عمليات التسميد</h1>

      {/* Filters */}
      <div style={{ display: "flex", marginBottom: "20px", gap: "10px" }}>
        <Form.Control
          type="date"
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
          onFocus={(e) => e.target.style.color = "#000"}
          onBlur={(e) => {
            if (!e.target.value) e.target.style.color = "#aaa";
          }}
          style={{ maxWidth: "250px", color: dateFilter ? "#000" : "#aaa" }}
        />
        <Form.Control
          type="text"
          placeholder="الترتيب حسب اسم السماد"
          value={nameFilter}
          onChange={(e) => setNameFilter(e.target.value)}
          style={{ maxWidth: "250px" }}
        />
        <Button 
          variant="outline-secondary" 
          onClick={() => {
            setDateFilter("");
            setNameFilter("");
          }}
          style={{ border: "1px solid darkgreen", color: "darkgreen" }}
        >
          مسح الترتيب
        </Button>
      </div>

      {/* Summary Card */}
      <div style={{ 
        backgroundColor: "white", 
        padding: "15px", 
        marginBottom: "20px", 
        borderRadius: "5px",
        border: "1px solid darkgreen"
      }}>
        <h4 style={{ color: "darkgreen" }}>الملخص</h4>
        <p style={{ fontSize: "18px" }}>
          <strong>إجمالي التسميد:</strong> ${totalFertilizations}
        </p>
        <p>
          <strong>عدد المعاملات:</strong> {filteredFertilizations.length}
        </p>
      </div>

      {/* Error Message */}
      {error && <Alert variant="danger">{error}</Alert>}

      {/* Loading Spinner */}
      {loading ? (
        <div className="text-center">
          <Spinner animation="border" role="status" style={{ color: "darkgreen" }}>
            <span className="visually-hidden">جاري التحميل...</span>
          </Spinner>
        </div>
      ) : (
        /* Fertilizations Table */
        <Table hover responsive>
          <thead style={{ backgroundColor: "darkgreen", color: "white" }}>
            <tr>
              <th>#</th>
              <th>اسم السماد</th>
              <th>اسم المنتج</th>
              <th>الموظف</th>
              <th>المبلغ ($)</th>
              <th>التاريخ</th>
              <th>الإجراءات</th>
            </tr>
          </thead>
          <tbody>
            {filteredFertilizations.map((fertilization, index) => (
              <tr key={fertilization.id}>
                <td>{index + 1}</td>
                <td>{fertilization.fertilizer?.name || "-"}</td>
                <td>{fertilization.products?.[0]?.name || "-"}</td>
                <td>{fertilization.employee_name}</td>
                <td>${fertilization.amount}</td>
                <td>{fertilization.transaction_date}</td>
                <td>
                  <Button 
                    variant="outline-success" 
                    size="sm" 
                    onClick={() => viewFertilizationDetails(fertilization)}
                  >
                    تفاصيل
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Details Modal */}
      <Modal show={showDetails} onHide={() => setShowDetails(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>تفاصيل التسميد</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedFertilization && (
            <>
              <p><strong>اسم السماد:</strong> {selectedFertilization.fertilizer?.name}</p>
              <p><strong>اسم المنتج:</strong> {selectedFertilization.products?.[0]?.name || "-"}</p>
              <p><strong>التصنيف:</strong> {selectedFertilization.products?.[0]?.classification || "-"}</p>
              <p><strong>الموظف:</strong> {selectedFertilization.employee_name}</p>
              <p><strong>المبلغ:</strong> ${selectedFertilization.amount}</p>
              <p><strong>التاريخ:</strong> {selectedFertilization.transaction_date}</p>
              <p><strong>ملاحظات:</strong> {selectedFertilization.notes || "لا توجد ملاحظات"}</p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetails(false)}>
            إغلاق
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default FertilizationsReport;
